import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Car, CheckCheck, Trash2, X } from 'lucide-react'
import { api } from '../api/client.ts'
import { Button } from './ui/button.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu.tsx'

interface CarNotification {
  id: string
  carId: number
  vin: string
  title: string
  message: string
  targetPath: string
  createdAt: string
  read: boolean
}

interface NotificationCenterProps {
  username: string
}

const POLL_INTERVAL = 20_000
const MAX_NOTIFICATIONS = 50

function readStoredNotifications(key: string): CarNotification[] {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]')
    return Array.isArray(value) ? value : []
  } catch {
    return []
  }
}

function formatNotificationDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function NotificationCenter({ username }: NotificationCenterProps) {
  const navigate = useNavigate()
  const storageKey = `drivebox.notifications.${username}`
  const seenCarsKey = `drivebox.notifications.seen-cars.${username}`
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<CarNotification[]>(() => readStoredNotifications(storageKey))

  const saveNotifications = useCallback((next: CarNotification[]) => {
    const limited = next.slice(0, MAX_NOTIFICATIONS)
    setNotifications(limited)
    localStorage.setItem(storageKey, JSON.stringify(limited))
  }, [storageKey])

  const syncCars = useCallback(async () => {
    try {
      const response: any = await api.cars.list(1000, 0, '')
      const cars = Array.isArray(response.data?.cars) ? response.data.cars : []
      const storedSeen = localStorage.getItem(seenCarsKey)
      const currentIds = cars.map((car: any) => Number(car.id)).filter(Number.isFinite)

      if (storedSeen === null) {
        localStorage.setItem(seenCarsKey, JSON.stringify(currentIds))
        return
      }

      let seenIds: number[] = []
      try {
        const parsed = JSON.parse(storedSeen)
        seenIds = Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : []
      } catch {
        seenIds = []
      }

      const seen = new Set(seenIds)
      const newCars = cars.filter((car: any) => Number.isFinite(Number(car.id)) && !seen.has(Number(car.id)))
      if (newCars.length > 0) {
        const stored = readStoredNotifications(storageKey)
        const existing = new Set(stored.map((notification) => notification.id))
        const additions = newCars
          .filter((car: any) => !existing.has(`car-${car.id}`))
          .map((car: any): CarNotification => ({
            id: `car-${car.id}`,
            carId: Number(car.id),
            vin: String(car.vin || ''),
            title: 'Новый автомобиль',
            message: `VIN ${car.vin || 'не указан'} добавлен в систему`,
            targetPath: `/cars?search=${encodeURIComponent(String(car.vin || ''))}`,
            createdAt: car.created_at || new Date().toISOString(),
            read: false,
          }))
        saveNotifications([...additions.reverse(), ...stored])
      }

      localStorage.setItem(seenCarsKey, JSON.stringify(Array.from(new Set([...seenIds, ...currentIds]))))
    } catch {
      return
    }
  }, [saveNotifications, seenCarsKey, storageKey])

  useEffect(() => {
    setNotifications(readStoredNotifications(storageKey))
    void syncCars()
    const interval = window.setInterval(syncCars, POLL_INTERVAL)
    const handleFocus = () => { void syncCars() }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) setNotifications(readStoredNotifications(storageKey))
    }
    window.addEventListener('focus', handleFocus)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('storage', handleStorage)
    }
  }, [storageKey, syncCars])

  const unreadCount = useMemo(
    () => notifications.reduce((count, notification) => count + (notification.read ? 0 : 1), 0),
    [notifications],
  )

  const openNotification = (notification: CarNotification) => {
    saveNotifications(notifications.map((item) => (
      item.id === notification.id ? { ...item, read: true } : item
    )))
    setOpen(false)
    navigate(notification.targetPath)
  }

  const removeNotification = (id: string) => {
    saveNotifications(notifications.filter((notification) => notification.id !== id))
  }

  const markAllRead = () => {
    saveNotifications(notifications.map((notification) => ({ ...notification, read: true })))
  }

  const clearAll = () => {
    saveNotifications([])
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 hover:bg-accent" aria-label="Уведомления">
          <Bell className="h-4 w-4" style={{ color: 'var(--figma-text-muted)' }} />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[360px] overflow-hidden p-0"
        style={{ background: 'var(--figma-surface)', borderColor: 'var(--figma-border)', maxWidth: 'calc(100vw - 24px)' }}
      >
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--figma-border)' }}>
          <div>
            <div className="text-sm font-semibold" style={{ color: 'var(--figma-text)' }}>Уведомления</div>
            <div className="mt-0.5 text-[11px]" style={{ color: 'var(--figma-text-muted)' }}>
              {unreadCount > 0 ? `Непрочитанных: ${unreadCount}` : 'Нет непрочитанных'}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] transition-colors hover:bg-white/5"
              style={{ color: 'var(--figma-accent)' }}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Прочитать все
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-8 text-center">
            <Bell className="mb-2 h-6 w-6" style={{ color: 'var(--figma-text-dim)' }} />
            <div className="text-sm" style={{ color: 'var(--figma-text-muted)' }}>Новых уведомлений нет</div>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="group relative flex border-b transition-colors hover:bg-white/[0.035] last:border-b-0"
                style={{
                  borderColor: 'var(--figma-border-subtle)',
                  background: notification.read ? 'transparent' : 'var(--figma-accent-glow)',
                }}
              >
                <button
                  type="button"
                  onClick={() => openNotification(notification)}
                  className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--figma-surface-3)' }}>
                    <Car className="h-4 w-4" style={{ color: 'var(--figma-accent)' }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-xs font-semibold" style={{ color: 'var(--figma-text)' }}>{notification.title}</span>
                      {!notification.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: 'var(--figma-accent)' }} />}
                    </span>
                    <span className="mt-1 block truncate text-[11px]" style={{ color: 'var(--figma-text-muted)' }}>{notification.message}</span>
                    <span className="mt-1 block text-[10px]" style={{ color: 'var(--figma-text-dim)' }}>{formatNotificationDate(notification.createdAt)}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => removeNotification(notification.id)}
                  className="mr-2 mt-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md opacity-60 transition-all hover:bg-white/5 hover:opacity-100"
                  style={{ color: 'var(--figma-text-muted)' }}
                  aria-label="Удалить уведомление"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {notifications.length > 0 && (
          <div className="border-t p-2" style={{ borderColor: 'var(--figma-border)' }}>
            <button
              type="button"
              onClick={clearAll}
              className="flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-xs transition-colors hover:bg-red-500/10"
              style={{ color: 'var(--figma-red)' }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Очистить уведомления
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
